import React, { lazy } from "react";
import {
  FaStar,
  FaCalendarAlt,
  FaCog,
  FaMobileAlt,
  FaUser,
  FaMailBulk,
  FaGift,
  FaUtensilSpoon,
  FaBell,
  FaLock,
  FaFilePdf,
  FaPiggyBank,
  FaHeart,
  FaUserTie,
  FaGem,
  FaClock,
  FaUsers,
  FaMoneyBill,
  FaGrinWink,
  FaEye,
  FaChair,
  FaEnvelope,
  FaGraduationCap,
  FaBullhorn,
  FaInfoCircle,
} from "react-icons/fa";

import {
  overviewSecondaryTopBar,
  accountSecondaryTopBar,
  settingsSecondaryTopBar,
  openinghoursSecondaryTopBar,
  maxArrivalsSecondaryTopBar,
  maxGroupsSecondaryTopBar,
  emailSecondaryTopBar,
  inboxSecondaryTopBar,
} from "./secondaryTopBars.js";

// Retry a dynamic import once — if a chunk fails (stale deploy), reload the page.
// Uses sessionStorage to prevent infinite reload loops.
const lazyRetry = (importFn) =>
  lazy(() =>
    importFn()
      .then((module) => {
        // Chunk loaded OK after a retry-reload → clear the flag
        if (sessionStorage.getItem("chunk_retry")) {
          sessionStorage.removeItem("chunk_retry");
        }
        return module;
      })
      .catch((err) => {
        const alreadyRetried = sessionStorage.getItem("chunk_retry");
        if (!alreadyRetried) {
          sessionStorage.setItem("chunk_retry", "1");
          window.location.reload();
          return new Promise(() => {}); // never resolves — page is reloading
        }
        // Already retried once — don't loop. Let error boundary handle it.
        sessionStorage.removeItem("chunk_retry");
        throw err;
      })
  );

// Lazy load all page components for code splitting
const CalendarComponent = lazyRetry(() => import("../Pages/Dashboard/CalendarMap/index.js"));
const DayList = lazyRetry(() => import("../Pages/Dashboard/DayList/index.js"));
const Profile = lazyRetry(() => import("../Pages/Profiel/index.js"));
const GiftCard = lazyRetry(() => import("../Pages/Cadeaubonnen/index.js"));
const GiftcardActivity = lazyRetry(() => import("../Pages/GiftcardActivity/index.js"));
const Layout = lazyRetry(() => import("../Pages/AppStore/index.js"));
const UwWebsite = lazyRetry(() => import("../Pages/UwWebsite/index.js"));
const Menu = lazyRetry(() => import("../Pages/Menu/index.js"));
const Mededeling = lazyRetry(() => import("../Pages/Mededeling/index.js"));
const Openingsuren = lazyRetry(() => import("../Pages/Openingsuren/index.js"));
const Uitzonderingen = lazyRetry(() => import("../Pages/Uitzonderingen/index.js"));
const UploadPdf = lazyRetry(() => import("../Pages/UploadPdf/index.js"));
const StripeDashboard = lazyRetry(() => import("../Pages/StripeDashboard/index.js"));
const MaxArrivals = lazyRetry(() => import("../Pages/MaxAankomsten/index.js"));
const MaxGroups = lazyRetry(() => import("../Pages/MaxGroepen/index.js"));
const EmailSettings = lazyRetry(() => import("../Pages/Instellingen/EmailSettings/index.js"));
const ObligatedFieldsPage = lazyRetry(() => import("../Pages/Instellingen/ObligatedFieldsPage.js"));
const Personeel = lazyRetry(() => import("../Pages/Personeel/index.js"));
const Instellingen = lazyRetry(() => import("../Pages/Instellingen/index.js"));
const Billing = lazyRetry(() => import("../Pages/Facturatie/index.js"));
const Subscription = lazyRetry(() => import("../Pages/Subscription/index.js"));
const Security = lazyRetry(() => import("../Pages/Security/index.js"));
const GiftcardBanners = lazyRetry(() => import("../Pages/GiftcardBanners/index.js"));
const ExtensionPage = lazyRetry(() => import("../Pages/Extension/index.js"));
const ConfirmationEmailContent = lazyRetry(() => import("../Pages/EmailContent/index.js").then(m => ({ default: m.ConfirmationEmailContent })));
const ReminderEmailContent = lazyRetry(() => import("../Pages/EmailContent/index.js").then(m => ({ default: m.ReminderEmailContent })));
const Contact = lazyRetry(() => import("../Pages/Contact/index.js"));
const GiftSending = lazyRetry(() => import("../Pages/GiftSending/index.js"));
const Referral = lazyRetry(() => import("../Pages/Referral/index.js"));
const Permissions = lazyRetry(() => import("../Pages/Permissions/index.js"));
const Review = lazyRetry(() => import("../Pages/Review/index.js"));
const Gallery = lazyRetry(() => import("../Pages/Gallery/index.js"));
const Training = lazyRetry(() => import("../Pages/Training/index.js"));
const Marketing = lazyRetry(() => import("../Pages/Marketing/index.js"));
const Newsletter = lazyRetry(() => import("../Pages/Newsletter/index.js"));
const EmailInbox = lazyRetry(() => import("../Pages/EmailInbox/index.js"));
const EmailDetail = lazyRetry(() => import("../Pages/EmailInbox/EmailDetail.js"));
const EmailKnowledgeBase = lazyRetry(() => import("../Pages/EmailInbox/KnowledgeBase.js"));
const EmailInboxSettings = lazyRetry(() => import("../Pages/EmailInbox/InboxSettings.js"));

const getMenuSettings = () => {
  try {
    const savedSettings = localStorage.getItem("menuSettings");
    return savedSettings ? JSON.parse(savedSettings) : {};
  } catch (error) {
    console.error("Error loading menu settings:", error);
    return {};
  }
};

const menuSettings = getMenuSettings();

const isMenuVisible = (path, defaultValue) => {
  return menuSettings.hasOwnProperty(path) ? menuSettings[path] : defaultValue;
};

// Helper function to get translation by key path (e.g., "sidebar.dashboard")
export const getTranslation = (translations, key, fallback = key) => {
  if (!key || !translations) return fallback;
  const keys = key.split('.');
  let value = translations;
  for (const k of keys) {
    value = value?.[k];
    if (value === undefined) return fallback;
  }
  return value || fallback;
};

const routesConfig = [
  {
    path: "/",
    element: <CalendarComponent />,
    labelKey: "sidebar.dashboard",
    icon: FaStar,
    isMenu: true,
    isMobile: true,
    isTab: true,
    secondaryTopBar: overviewSecondaryTopBar,
  },
  {
    path: "/tafelplan",
    element: null,
    labelKey: "apps.tablePlan",
    icon: FaChair,
    isMenu: true,
    isMobile: false,
    isTab: false,
    isExternalApp: true,
    appUrl: "https://mateza-tables.vercel.app/jwt-auth/",
    appDomain: "mateza-tables.vercel.app",
  },
  {
    path: "/settings/billing",
    element: <Billing />,
    labelKey: "sidebar.billing",
    icon: FaMoneyBill,
    isMenu: false,
    isMobile: false,
    isTab: false,
    secondaryTopBar: settingsSecondaryTopBar,
  },
  {
    path: "/review",
    element: <Review />,
    labelKey: "sidebar.reviews",
    icon: FaGrinWink,
    isMenu: true,
    isMobile: false,
    isTab: false,
  },
  {
    path: "/menu",
    element: <Menu />,
    labelKey: "sidebar.menu",
    icon: FaUtensilSpoon,
    isMenu: true,
    isMobile: true,
    isTab: false,
  },
  {
    path: "/mededelingen",
    element: <Mededeling />,
    labelKey: "sidebar.notification",
    icon: FaBell,
    isMenu: true,
    isMobile: false,
    isTab: false,
  },
  {
    path: "/max-arrivals",
    element: <MaxArrivals mealType="breakfast" />,
    labelKey: "sidebar.arrivals",
    icon: FaClock,
    isMenu: true,
    isMobile: false,
    isTab: false,
    secondaryTopBar: maxArrivalsSecondaryTopBar,
  },
  {
    path: "/max-arrivals/lunch",
    element: <MaxArrivals mealType="lunch" />,
    labelKey: "sidebar.arrivals",
    icon: FaClock,
    isMenu: false,
    isMobile: false,
    isTab: false,
    secondaryTopBar: maxArrivalsSecondaryTopBar,
  },
  {
    path: "/max-arrivals/dinner",
    element: <MaxArrivals mealType="dinner" />,
    labelKey: "sidebar.arrivals",
    icon: FaClock,
    isMenu: false,
    isMobile: false,
    isTab: false,
    secondaryTopBar: maxArrivalsSecondaryTopBar,
  },
  {
    path: "/max-groups",
    element: <MaxGroups mealType="breakfast" />,
    labelKey: "sidebar.groups",
    icon: FaUsers,
    isMenu: true,
    isMobile: false,
    isTab: false,
    secondaryTopBar: maxGroupsSecondaryTopBar,
  },
  {
    path: "/max-groups/lunch",
    element: <MaxGroups mealType="lunch" />,
    labelKey: "sidebar.groups",
    icon: FaUsers,
    isMenu: false,
    isMobile: false,
    isTab: false,
    secondaryTopBar: maxGroupsSecondaryTopBar,
  },
  {
    path: "/max-groups/dinner",
    element: <MaxGroups mealType="dinner" />,
    labelKey: "sidebar.groups",
    icon: FaUsers,
    isMenu: false,
    isMobile: false,
    isTab: false,
    secondaryTopBar: maxGroupsSecondaryTopBar,
  },
  {
    path: "/day",
    element: <DayList />,
    labelKey: "sidebar.dailyOverview",
    icon: FaStar,
    isMenu: false,
    isMobile: false,
    isTab: false,
    secondaryTopBar: overviewSecondaryTopBar,
  },
  {
    path: "/uitzonderingen",
    element: <Uitzonderingen />,
    labelKey: "sidebar.exceptions",
    icon: FaLock,
    isMenu: true,
    isMobile: true,
    isTab: false,
  },
  {
    path: "/openinghours",
    element: <Openingsuren mealType="breakfast" />,
    labelKey: "sidebar.openingHours",
    icon: FaCalendarAlt,
    isMenu: false,
    isMobile: false,
    isTab: false,
    secondaryTopBar: openinghoursSecondaryTopBar,
  },
  {
    path: "/openinghours/lunch",
    element: <Openingsuren mealType="lunch" />,
    labelKey: "sidebar.openingHours",
    icon: FaCalendarAlt,
    isMenu: false,
    isMobile: false,
    isTab: false,
    secondaryTopBar: openinghoursSecondaryTopBar,
  },
  {
    path: "/openinghours/dinner",
    element: <Openingsuren mealType="dinner" />,
    labelKey: "sidebar.openingHours",
    icon: FaCalendarAlt,
    isMenu: true,
    isMobile: false,
    isTab: false,
    secondaryTopBar: openinghoursSecondaryTopBar,
  },
  {
    path: "/account",
    element: <Profile />,
    labelKey: "sidebar.account",
    icon: FaUser,
    isMenu: false,
    isMobile: false,
    isTab: false,
    secondaryTopBar: accountSecondaryTopBar,
  },
  {
    path: "/contacts",
    element: <Contact />,
    labelKey: "sidebar.contacts",
    icon: FaUser,
    isMenu: true,
    isMobile: true,
    isTab: true,
  },
  {
    path: "/about",
    element: <EmailKnowledgeBase />,
    labelKey: "sidebar.about",
    icon: FaInfoCircle,
    isMenu: true,
    isMobile: true,
    isTab: false,
  },
  {
    path: "/inbox",
    element: <EmailInbox />,
    labelKey: "sidebar.inbox",
    icon: FaEnvelope,
    isMenu: true,
    isMobile: true,
    isTab: true,
    secondaryTopBar: inboxSecondaryTopBar,
  },
  {
    path: "/inbox/settings",
    element: <EmailInboxSettings />,
    labelKey: "sidebar.inboxSettings",
    icon: FaEnvelope,
    isMenu: false,
    isMobile: false,
    isTab: false,
    secondaryTopBar: inboxSecondaryTopBar,
  },
  {
    path: "/inbox/:id",
    element: <EmailDetail />,
    labelKey: "sidebar.inbox",
    icon: FaEnvelope,
    isMenu: false,
    isMobile: false,
    isTab: false,
    secondaryTopBar: inboxSecondaryTopBar,
  },
  {
    path: "/referral",
    element: <Referral />,
    labelKey: "sidebar.goldenTicket",
    titleKey: "sidebar.goldenTicketPageTitle",
    icon: FaHeart,
    isMenu: false,
    isMobile: false,
    isTab: false,
  },
  {
    path: "/account/team",
    element: <Personeel />,
    labelKey: "sidebar.staff",
    icon: FaUserTie,
    isMenu: true,
    isMobile: true,
    isTab: true,
  },
  {
    path: "/security",
    element: <Security />,
    labelKey: "sidebar.application",
    icon: FaMobileAlt,
    isMenu: true,
    isMobile: true,
    isTab: true,
  },
  {
    path: "/settings/pdf-menu",
    element: <UploadPdf />,
    labelKey: "sidebar.pdfMenu",
    icon: FaFilePdf,
    isMenu: false,
    isMobile: false,
    isTab: false,
    secondaryTopBar: settingsSecondaryTopBar,
  },
  {
    path: "/settings/permissions",
    element: <Permissions />,
    labelKey: "sidebar.permissions",
    icon: FaLock,
    isMenu: false,
    isMobile: false,
    isTab: false,
    secondaryTopBar: settingsSecondaryTopBar,
  },
  {
    path: "/settings/fields",
    element: <ObligatedFieldsPage />,
    labelKey: "secondary.fields",
    icon: FaCog,
    isMenu: false,
    isMobile: false,
    isTab: false,
    secondaryTopBar: settingsSecondaryTopBar,
  },
  {
    path: "/settings",
    element: <Instellingen />,
    labelKey: "sidebar.settings",
    icon: FaCog,
    isMenu: false,
    isMobile: false,
    isTab: false,
    secondaryTopBar: settingsSecondaryTopBar,
  },
  {
    path: "/stripe-dashboard",
    element: <StripeDashboard />,
    labelKey: "sidebar.profit",
    icon: FaPiggyBank,
    isMenu: true,
    isMobile: false,
    isTab: false,
  },
  {
    path: "/cadeaubonnen",
    element: <GiftCard />,
    labelKey: "sidebar.giftCards",
    icon: FaGift,
    isMenu: true,
    isMobile: true,
    isTab: true,
  },
  {
    path: "/gift-sending",
    element: <GiftSending />,
    labelKey: "sidebar.giftSending",
    icon: FaGift,
    isMenu: true,
    isMobile: false,
    isTab: false,
  },
  {
    path: "/cadeaubonnen/banners",
    element: <GiftcardBanners />,
    labelKey: "sidebar.giftCardBanners",
    icon: FaEye,
    isMenu: true,
    isMobile: false,
    isTab: false,
  },
  {
    path: "/cadeaubonnen/activity",
    element: <GiftcardActivity />,
    labelKey: "sidebar.giftCardActivity",
    icon: FaClock,
    isMenu: true,
    isMobile: true,
    isTab: false,
  },
  {
    path: "/restaurant-page",
    element: <UwWebsite />,
    labelKey: "sidebar.website",
    icon: FaGem,
    isMenu: false,
    isMobile: false,
    isTab: false,
  },
  {
    path: "/mateza-cloud-apps",
    element: <Layout />,
    labelKey: "sidebar.bonusApps",
    icon: FaHeart,
    isMenu: true,
    isMobile: false,
    isTab: false,
  },
  {
    path: "/settings/email",
    element: <EmailSettings />,
    labelKey: "sidebar.emailNotifications",
    icon: FaMailBulk,
    isMenu: false,
    isMobile: false,
    isTab: false,
    secondaryTopBar: settingsSecondaryTopBar,
  },
  {
    path: "/email",
    element: <ConfirmationEmailContent />,
    labelKey: "secondary.emailContent",
    icon: FaEnvelope,
    isMenu: true,
    isMobile: false,
    isTab: false,
    secondaryTopBar: emailSecondaryTopBar,
  },
  {
    path: "/email/reminder",
    element: <ReminderEmailContent />,
    labelKey: "secondary.reminderEmail",
    icon: FaEnvelope,
    isMenu: false,
    isMobile: false,
    isTab: false,
    secondaryTopBar: emailSecondaryTopBar,
  },
  {
    path: "/email/newsletter",
    element: <Newsletter />,
    labelKey: "secondary.newsletter",
    icon: FaEnvelope,
    isMenu: true,
    isMobile: false,
    isTab: false,
  },
  {
    path: "/email/extension",
    element: <ExtensionPage />,
    labelKey: "secondary.emailExtension",
    icon: FaEnvelope,
    isMenu: true,
    isMobile: false,
    isTab: false,
  },
  {
    path: "/marketing",
    element: <Marketing />,
    labelKey: "sidebar.marketing",
    icon: FaBullhorn,
    isMenu: true,
    isMobile: false,
    isTab: false,
  },
  {
    path: "/subscription",
    element: <Subscription />,
    labelKey: "sidebar.subscription",
    icon: FaStar,
    isMenu: true,
    isMobile: true,
    isTab: false,
  },
  {
    path: "/gallery",
    element: <Gallery />,
    labelKey: "sidebar.gallery",
    icon: FaEye,
    isMenu: true,
    isMobile: false,
    isTab: false,
  },
  {
    path: "/training",
    element: <Training />,
    labelKey: "sidebar.training",
    icon: FaGraduationCap,
    isMenu: true,
    isMobile: false,
    isTab: false,
  },
];

export default routesConfig;
